import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, Crown, SpinnerGap, Trash, UserPlus, UsersThree } from '@phosphor-icons/react';
import * as api from '../../api/client';

interface WorkspaceMembersPanelProps {
  workspaceId: string | null;
}

const ROLE_LABELS: Record<api.WorkspaceMemberRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  editor: 'Editor',
  viewer: 'Viewer',
};

const MANAGEABLE_ROLES: Exclude<api.WorkspaceMemberRole, 'owner'>[] = [
  'admin',
  'editor',
  'viewer',
];

export default function WorkspaceMembersPanel({ workspaceId }: WorkspaceMembersPanelProps) {
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState<api.WorkspaceMember[]>([]);
  const [myRole, setMyRole] = useState<api.WorkspaceMemberRole | null>(null);
  const [email, setEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<Exclude<api.WorkspaceMemberRole, 'owner'>>('editor');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const canManage = myRole === 'owner' || myRole === 'admin';
  const isOwner = myRole === 'owner';

  const reload = useCallback(async () => {
    if (!workspaceId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await api.listWorkspaceMembers(workspaceId);
      setMembers(res.members);
      setMyRole(res.me.role);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load members.');
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const sortedMembers = useMemo(() => members, [members]);

  const flashSaved = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 1600);
  };

  const invite = async () => {
    if (!workspaceId || !email.trim()) return;
    setBusy('invite');
    setError(null);
    try {
      const member = await api.inviteWorkspaceMember(workspaceId, {
        email: email.trim(),
        role: inviteRole,
      });
      setMembers(prev => {
        const rest = prev.filter(m => m.user_id !== member.user_id);
        return [...rest, member];
      });
      setEmail('');
      flashSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to invite member.');
    } finally {
      setBusy(null);
    }
  };

  const changeRole = async (
    member: api.WorkspaceMember,
    role: Exclude<api.WorkspaceMemberRole, 'owner'>,
  ) => {
    if (!workspaceId || member.role === role) return;
    setBusy(`role:${member.user_id}`);
    setError(null);
    try {
      const updated = await api.setWorkspaceMemberRole(workspaceId, member.user_id, role);
      setMembers(prev => prev.map(m => (m.user_id === updated.user_id ? updated : m)));
      flashSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update role.');
    } finally {
      setBusy(null);
    }
  };

  const remove = async (member: api.WorkspaceMember) => {
    if (!workspaceId) return;
    if (!confirm(`Remove ${member.display_name || member.email || member.user_id} from this workspace?`)) return;
    setBusy(`remove:${member.user_id}`);
    setError(null);
    try {
      await api.removeWorkspaceMember(workspaceId, member.user_id);
      setMembers(prev => prev.filter(m => m.user_id !== member.user_id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove member.');
    } finally {
      setBusy(null);
    }
  };

  const transferOwner = async (member: api.WorkspaceMember) => {
    if (!workspaceId) return;
    if (!confirm(`Transfer workspace ownership to ${member.display_name || member.email || member.user_id}?`)) return;
    setBusy(`transfer:${member.user_id}`);
    setError(null);
    try {
      const next = await api.transferWorkspaceOwner(workspaceId, member.user_id);
      setMembers(next);
      setMyRole('admin');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to transfer ownership.');
    } finally {
      setBusy(null);
    }
  };

  const sectionStyle: React.CSSProperties = {
    background: 'var(--bg-surface)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    padding: '20px 24px',
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 12px',
    fontSize: 'var(--fs-base)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    background: 'var(--bg-primary)',
    color: 'var(--text-primary)',
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box',
  };

  const buttonStyle = (primary: boolean, disabled = false): React.CSSProperties => ({
    padding: '8px 14px',
    fontSize: 'var(--fs-sm)',
    fontWeight: 600,
    border: primary ? 'none' : '1px solid var(--border)',
    borderRadius: 8,
    background: primary ? '#0033FF' : 'var(--bg-primary)',
    color: primary ? '#fff' : 'var(--text-secondary)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.55 : 1,
    fontFamily: 'inherit',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  });

  if (!workspaceId) return null;

  return (
    <div style={sectionStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <UsersThree size={16} weight="light" style={{ color: 'var(--text-muted)' }} />
        <span style={{ fontSize: 'var(--fs-lg)', fontWeight: 600, color: 'var(--text-primary)' }}>
          Workspace members
        </span>
        {saved && <Check size={14} weight="bold" style={{ color: '#16a34a', marginLeft: 'auto' }} />}
      </div>
      <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)', margin: '0 0 16px' }}>
        Workspace members can open decks in this workspace. Viewers are read-only.
      </p>

      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)' }}>
          <SpinnerGap size={16} weight="light" className="animate-spin" />
          <span style={{ fontSize: 'var(--fs-sm)' }}>Loading members...</span>
        </div>
      ) : (
        <>
          {canManage && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 112px auto', gap: 8, marginBottom: 16 }}>
              <input
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="name@company.com"
                style={inputStyle}
              />
              <select
                value={inviteRole}
                onChange={e => setInviteRole(e.target.value as Exclude<api.WorkspaceMemberRole, 'owner'>)}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                {MANAGEABLE_ROLES.map(role => (
                  <option key={role} value={role}>{ROLE_LABELS[role]}</option>
                ))}
              </select>
              <button
                disabled={busy === 'invite' || !email.trim()}
                onClick={() => void invite()}
                style={buttonStyle(true, busy === 'invite' || !email.trim())}
              >
                {busy === 'invite' ? <SpinnerGap size={14} weight="light" className="animate-spin" /> : <UserPlus size={14} weight="light" />}
                Invite
              </button>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {sortedMembers.map(member => {
              const name = member.display_name || member.email || member.user_id;
              const isMemberOwner = member.role === 'owner';
              const rowBusy = busy?.endsWith(member.user_id);
              return (
                <div
                  key={member.user_id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: canManage ? '1fr 128px auto' : '1fr 96px',
                    gap: 10,
                    alignItems: 'center',
                    padding: '10px 0',
                    borderTop: '1px solid var(--border)',
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {name}
                      </span>
                      {isMemberOwner && <Crown size={13} weight="light" style={{ color: '#d97706', flexShrink: 0 }} />}
                    </div>
                    {member.email && member.display_name && (
                      <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {member.email}
                      </div>
                    )}
                  </div>

                  {canManage && !isMemberOwner ? (
                    <select
                      value={member.role}
                      disabled={rowBusy}
                      onChange={e => void changeRole(member, e.target.value as Exclude<api.WorkspaceMemberRole, 'owner'>)}
                      style={{ ...inputStyle, padding: '6px 8px', fontSize: 'var(--fs-sm)', cursor: rowBusy ? 'default' : 'pointer' }}
                    >
                      {MANAGEABLE_ROLES.map(role => (
                        <option key={role} value={role}>{ROLE_LABELS[role]}</option>
                      ))}
                    </select>
                  ) : (
                    <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>
                      {ROLE_LABELS[member.role]}
                    </div>
                  )}

                  {canManage && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
                      {isOwner && !isMemberOwner && (
                        <button
                          disabled={rowBusy}
                          title="Transfer ownership"
                          onClick={() => void transferOwner(member)}
                          style={buttonStyle(false, rowBusy)}
                        >
                          <Crown size={13} weight="light" />
                        </button>
                      )}
                      {!isMemberOwner && (
                        <button
                          disabled={rowBusy}
                          title="Remove"
                          onClick={() => void remove(member)}
                          style={buttonStyle(false, rowBusy)}
                        >
                          {rowBusy ? <SpinnerGap size={13} weight="light" className="animate-spin" /> : <Trash size={13} weight="light" />}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {error && (
        <div style={{ marginTop: 12, fontSize: 'var(--fs-sm)', color: '#b91c1c' }}>
          {error}
        </div>
      )}
    </div>
  );
}
